import React, { useEffect, useLayoutEffect, useState, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  CloseTabIcon,
  ChevronDownIcon,
  PlusIcon,
  ClockOutlineIcon,
  CheckmarkIcon,
  ArchiveIcon,
  MapIcon,
} from 'components/common/Icons';
import styles from './styles.module.scss';

interface AddressItem {
  city?: string;
  country?: string;
  country_code?: string;
  full_address?: string;
}

export interface SearchTemplate {
  id: string;
  template_title: string;
  loading_addresses?: AddressItem[];
  unloading_addresses?: AddressItem[];
  filter_count?: number;
  hasNewItems?: boolean;
  notifications_enabled?: boolean;
  notification_sound_enabled?: boolean;
}

interface RootState {
  settings: {
    lang: string;
  };
  auth: {
    authenticated: boolean;
  };
}

export interface SearchTabsConfig {
  fetchTemplates: (params: { lang: string; limit: number }) => Promise<{ data: { items: SearchTemplate[] } }>;
  deleteTemplate: (templateId: string) => Promise<unknown>;
  updateTemplate: (templateId: string, data: Record<string, unknown>) => Promise<unknown>;
  getTemplate: (templateId: string, lang: string) => Promise<{ data: { data: Record<string, unknown> } }>;
  prepareUpdateData: (template: Record<string, unknown>) => Record<string, unknown>;
  translationPrefix: string;
  mapRoute: string;
}

export interface SearchTabsProps {
  config: SearchTabsConfig;
  activeTemplateId: string | null;
  onSelectTemplate: (templateId: string | null) => void;
  onAddTemplate: () => void;
  newItemsCounts?: Record<string, number>;
  isCreatingNew?: boolean;
  onCancelNewTemplate?: () => void;
  refreshTrigger?: number;
  activeStatusFilter?: string | null;
  onStatusFilterChange?: (status: string | null) => void;
  onTemplatesLoaded?: (templates: SearchTemplate[]) => void;
}

export const NEW_TEMPLATE_ID = 'new-template';

const SearchTabs: React.FC<SearchTabsProps> = ({
  config,
  activeTemplateId,
  onSelectTemplate,
  onAddTemplate,
  newItemsCounts = {},
  isCreatingNew = false,
  onCancelNewTemplate,
  refreshTrigger,
  activeStatusFilter,
  onStatusFilterChange,
  onTemplatesLoaded,
}) => {
  const { t } = useTranslation();
  const lang = useSelector((state: RootState) => state.settings.lang);
  const authenticated = useSelector((state: RootState) => state.auth.authenticated);
  const [templates, setTemplates] = useState<SearchTemplate[]>([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [visibleCount, setVisibleCount] = useState<number | null>(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [openMenus, setOpenMenus] = useState<Record<string, boolean>>({});
  const [menuPositions, setMenuPositions] = useState<Record<string, { top: number; left: number }>>({});
  const [dropdownOpenMenus, setDropdownOpenMenus] = useState<Record<string, boolean>>({});
  const [dropdownMenuPositions, setDropdownMenuPositions] = useState<Record<string, { top: number; left: number }>>({});
  const [menuReady, setMenuReady] = useState<Record<string, boolean>>({});
  const [dropdownMenuReady, setDropdownMenuReady] = useState<Record<string, boolean>>({});

  const dropdownRef = useRef<HTMLDivElement>(null);
  const dropdownBtnRef = useRef<HTMLButtonElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const tabsWrapperRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const statusFiltersRef = useRef<HTMLDivElement>(null);
  const addBtnMeasureRef = useRef<HTMLButtonElement>(null);
  const dropdownBtnMeasureRef = useRef<HTMLButtonElement>(null);
  const menuBtnRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const menuRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const dropdownMenuBtnRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const dropdownMenuRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const fetchTemplates = useCallback(async () => {
    if (!authenticated) return;

    try {
      const response = await config.fetchTemplates({ lang, limit: 100 });
      const loadedTemplates = response.data.items || [];
      setTemplates(loadedTemplates);
      onTemplatesLoaded?.(loadedTemplates);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    }
  }, [lang, authenticated, config, onTemplatesLoaded]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates, refreshTrigger]);

  // Calculate how many templates fit in the available space
  const calculateVisibleCount = useCallback(() => {
    if (!containerRef.current || !measureRef.current || !tabsWrapperRef.current) return;

    // Calculate available width for tabs
    const containerStyle = window.getComputedStyle(containerRef.current);
    const containerWidth = containerRef.current.offsetWidth;
    const paddingLeft = parseFloat(containerStyle.paddingLeft) || 0;
    const paddingRight = parseFloat(containerStyle.paddingRight) || 0;
    const statusFiltersWidth = statusFiltersRef.current?.offsetWidth || 0;

    // Gap between tabsWrapper and statusFilters (minimum spacing)
    const sectionGap = 16;
    const availableWidth = containerWidth - paddingLeft - paddingRight - statusFiltersWidth - sectionGap;

    // Get gap between tabs from tabsWrapper styles
    const wrapperStyle = window.getComputedStyle(tabsWrapperRef.current);
    const tabGap = parseFloat(wrapperStyle.gap) || 0;

    // Measure elements from the hidden measure container
    const allRoutesTab = measureRef.current.querySelector('[data-measure="all-routes"]') as HTMLElement;
    const templateTabs = measureRef.current.querySelectorAll('[data-measure="template"]');
    const newTemplateTab = measureRef.current.querySelector('[data-measure="new-template"]') as HTMLElement;
    const addBtn = addBtnMeasureRef.current;
    const dropdownBtn = dropdownBtnMeasureRef.current;

    const allRoutesWidth = allRoutesTab?.offsetWidth || 0;
    const addBtnWidth = addBtn?.offsetWidth || 0;
    const dropdownBtnWidth = dropdownBtn?.offsetWidth || 0;
    const newTemplateWidth = newTemplateTab?.offsetWidth || 0;

    // Start with "All routes" tab
    let usedWidth = allRoutesWidth;

    // Reserve space for end elements (add button or new template tab)
    const endElementWidth = isCreatingNew ? newTemplateWidth : addBtnWidth;

    let count = 0;

    for (let i = 0; i < templateTabs.length; i++) {
      const tabWidth = (templateTabs[i] as HTMLElement).offsetWidth;
      const remainingTemplates = templates.length - count - 1;
      const needsDropdown = remainingTemplates > 0;

      // Calculate total width if we add this tab
      const widthWithThisTab = usedWidth + tabGap + tabWidth;
      const reservedForEnd = endElementWidth + (needsDropdown ? tabGap + dropdownBtnWidth : 0);

      if (widthWithThisTab + tabGap + reservedForEnd > availableWidth) {
        break;
      }

      usedWidth = widthWithThisTab;
      count++;
    }

    setVisibleCount(count);
  }, [templates.length, isCreatingNew]);

  useEffect(() => {
    // Small delay to ensure DOM is ready
    const timer = setTimeout(calculateVisibleCount, 50);
    return () => clearTimeout(timer);
  }, [calculateVisibleCount, templates]);

  useEffect(() => {
    const handleResize = () => {
      calculateVisibleCount();
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [calculateVisibleCount]);

  // Watch for container size changes (e.g., when chat panel opens/closes)
  useEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver(() => {
      calculateVisibleCount();
    });

    resizeObserver.observe(containerRef.current);
    return () => resizeObserver.disconnect();
  }, [calculateVisibleCount]);

  // Close all menus when templates change or visible count changes
  useEffect(() => {
    setOpenMenus({});
    setDropdownOpenMenus({});
    setMenuReady({});
    setDropdownMenuReady({});
    setIsDropdownOpen(false);
  }, [templates.length, visibleCount]);

  // Close dropdown and menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(target) &&
        dropdownBtnRef.current &&
        !dropdownBtnRef.current.contains(target)
      ) {
        setIsDropdownOpen(false);
      }

      // Check if clicked outside any menu (both the button and the menu popup itself)
      const clickedOutsideMenus = Object.keys(openMenus).every(templateId => {
        const menuBtn = menuBtnRefs.current[templateId];
        const menu = menuRefs.current[templateId];
        const outsideBtn = !menuBtn || !menuBtn.contains(target);
        const outsideMenu = !menu || !menu.contains(target);
        return outsideBtn && outsideMenu;
      });

      const clickedOutsideDropdownMenus = Object.keys(dropdownOpenMenus).every(templateId => {
        const menuBtn = dropdownMenuBtnRefs.current[templateId];
        const menu = dropdownMenuRefs.current[templateId];
        const outsideBtn = !menuBtn || !menuBtn.contains(target);
        const outsideMenu = !menu || !menu.contains(target);
        return outsideBtn && outsideMenu;
      });

      if (clickedOutsideMenus) {
        setOpenMenus({});
        setMenuReady({});
      }

      if (clickedOutsideDropdownMenus) {
        setDropdownOpenMenus({});
        setDropdownMenuReady({});
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openMenus, dropdownOpenMenus]);

  // Calculate dropdown position
  const updateDropdownPosition = useCallback(() => {
    if (dropdownBtnRef.current) {
      const rect = dropdownBtnRef.current.getBoundingClientRect();
      let left = rect.left;
      let top = rect.bottom + 5;
      const dropdownWidth = dropdownRef.current?.offsetWidth || 250;
      const dropdownHeight = dropdownRef.current?.offsetHeight || 300;
      if (left + dropdownWidth > window.innerWidth) {
        left = window.innerWidth - dropdownWidth - 10; // 10px margin
      }
      if (left < 0) {
        left = 10; // minimum margin
      }
      if (top + dropdownHeight > window.innerHeight) {
        top = rect.top - dropdownHeight - 5; // position above
      }
      setDropdownPosition({ top, left });
    }
  }, []);

  // Calculate menu position (called after menu is rendered)
  const updateMenuPosition = useCallback((templateId: string) => {
    const btn = menuBtnRefs.current[templateId];
    const menu = menuRefs.current[templateId];
    if (btn && menu) {
      const rect = btn.getBoundingClientRect();
      let left = rect.left;
      let top = rect.bottom + 5;
      const menuWidth = menu.offsetWidth;
      const menuHeight = menu.offsetHeight;
      if (left + menuWidth > window.innerWidth) {
        left = window.innerWidth - menuWidth - 10;
      }
      if (left < 0) {
        left = 10;
      }
      if (top + menuHeight > window.innerHeight) {
        top = rect.top - menuHeight - 5;
      }
      setMenuPositions(prev => ({
        ...prev,
        [templateId]: { top, left }
      }));
      setMenuReady(prev => ({ ...prev, [templateId]: true }));
    }
  }, []);

  // Calculate dropdown menu position (called after menu is rendered)
  const updateDropdownMenuPosition = useCallback((templateId: string) => {
    const btn = dropdownMenuBtnRefs.current[templateId];
    const menu = dropdownMenuRefs.current[templateId];
    if (btn && menu) {
      const rect = btn.getBoundingClientRect();
      let left = rect.left;
      let top = rect.bottom + 5;
      const menuWidth = menu.offsetWidth;
      const menuHeight = menu.offsetHeight;
      if (left + menuWidth > window.innerWidth) {
        left = window.innerWidth - menuWidth - 10;
      }
      if (left < 0) {
        left = 10;
      }
      if (top + menuHeight > window.innerHeight) {
        top = rect.top - menuHeight - 5;
      }
      setDropdownMenuPositions(prev => ({
        ...prev,
        [templateId]: { top, left }
      }));
      setDropdownMenuReady(prev => ({ ...prev, [templateId]: true }));
    }
  }, []);

  // Update position when dropdown opens or window scrolls/resizes
  useEffect(() => {
    if (isDropdownOpen) {
      updateDropdownPosition();
      window.addEventListener('scroll', updateDropdownPosition, true);
      window.addEventListener('resize', updateDropdownPosition);
      return () => {
        window.removeEventListener('scroll', updateDropdownPosition, true);
        window.removeEventListener('resize', updateDropdownPosition);
      };
    }
  }, [isDropdownOpen, updateDropdownPosition]);

  // Update menu positions on scroll/resize
  useEffect(() => {
    const openMenuIds = Object.keys(openMenus).filter(id => openMenus[id]);
    if (openMenuIds.length > 0) {
      const updateAllMenuPositions = () => {
        openMenuIds.forEach(id => updateMenuPosition(id));
      };
      window.addEventListener('scroll', updateAllMenuPositions, true);
      window.addEventListener('resize', updateAllMenuPositions);
      return () => {
        window.removeEventListener('scroll', updateAllMenuPositions, true);
        window.removeEventListener('resize', updateAllMenuPositions);
      };
    }
  }, [openMenus, updateMenuPosition]);

  // Measure and position menus after render
  useLayoutEffect(() => {
    const openMenuIds = Object.keys(openMenus).filter(id => openMenus[id] && !menuReady[id]);
    openMenuIds.forEach(id => updateMenuPosition(id));
  }, [openMenus, menuReady, updateMenuPosition]);

  // Update dropdown menu positions on scroll/resize
  useEffect(() => {
    const openMenuIds = Object.keys(dropdownOpenMenus).filter(id => dropdownOpenMenus[id]);
    if (openMenuIds.length > 0) {
      const updateAllDropdownMenuPositions = () => {
        openMenuIds.forEach(id => updateDropdownMenuPosition(id));
      };
      window.addEventListener('scroll', updateAllDropdownMenuPositions, true);
      window.addEventListener('resize', updateAllDropdownMenuPositions);
      return () => {
        window.removeEventListener('scroll', updateAllDropdownMenuPositions, true);
        window.removeEventListener('resize', updateAllDropdownMenuPositions);
      };
    }
  }, [dropdownOpenMenus, updateDropdownMenuPosition]);

  // Measure and position dropdown menus after render
  useLayoutEffect(() => {
    const openMenuIds = Object.keys(dropdownOpenMenus).filter(id => dropdownOpenMenus[id] && !dropdownMenuReady[id]);
    openMenuIds.forEach(id => updateDropdownMenuPosition(id));
  }, [dropdownOpenMenus, dropdownMenuReady, updateDropdownMenuPosition]);

  const formatLocation = (template: SearchTemplate) => {
    const loadingAddr = template.loading_addresses?.[0];
    const unloadingAddr = template.unloading_addresses?.[0];

    const from = loadingAddr?.city || loadingAddr?.country || '';
    const to = unloadingAddr?.city || unloadingAddr?.country || '';

    const fromCountry = loadingAddr?.country_code?.toUpperCase() || null;
    const toCountry = unloadingAddr?.country_code?.toUpperCase() || null;

    return { from, to, fromCountry, toCountry };
  };

  const handleRemoveTemplate = async (e: React.MouseEvent, templateId: string) => {
    e.stopPropagation();
    try {
      await config.deleteTemplate(templateId);
      setTemplates((prev) => {
        const updated = prev.filter((t) => t.id !== templateId);
        onTemplatesLoaded?.(updated);
        return updated;
      });
      if (activeTemplateId === templateId) {
        onSelectTemplate(null);
      }
    } catch (error) {
      console.error('Failed to delete template:', error);
    }
  };

  const handleCancelNewTemplate = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onCancelNewTemplate) {
      onCancelNewTemplate();
    }
  };

  const handleOpenMap = (e: React.MouseEvent, templateId: string) => {
    e.stopPropagation();
    window.open(`${config.mapRoute}/${templateId}`, '_blank');
  };

  const handleToggleNotifications = async (e: React.MouseEvent, templateId: string) => {
    e.stopPropagation();
    const template = templates.find(t => t.id === templateId);
    if (!template) return;

    // Default (undefined) is considered enabled, so we check if it's explicitly false
    const isCurrentlyMuted = template.notification_sound_enabled === false;
    const newValue = isCurrentlyMuted; // If muted, enable (true); if enabled/undefined, disable (false)

    try {
      // Fetch full template data
      const response = await config.getTemplate(templateId, lang);
      const fullTemplate = response.data.data;

      // Prepare update data using the model's method and override notification fields
      const updateData = config.prepareUpdateData({
        ...fullTemplate,
        notification_sound_enabled: newValue,
      });

      await config.updateTemplate(templateId, updateData);
      setTemplates(prev => {
        const updated = prev.map(t =>
          t.id === templateId
            ? { ...t, notification_sound_enabled: newValue }
            : t
        );
        onTemplatesLoaded?.(updated);
        return updated;
      });
    } catch (error) {
      console.error('Failed to toggle notifications:', error);
    }
  };

  const handleSelectFromDropdown = (templateId: string) => {
    onSelectTemplate(templateId);
    setIsDropdownOpen(false);
    setOpenMenus({});
    setDropdownOpenMenus({});
    setMenuReady({});
    setDropdownMenuReady({});
  };

  const isAllRoutesActive = activeTemplateId === null && !isCreatingNew && !activeStatusFilter;
  const isNewTemplateActive = activeTemplateId === NEW_TEMPLATE_ID || isCreatingNew;

  // Use all templates if visibleCount not calculated yet
  const effectiveVisibleCount = visibleCount ?? templates.length;

  const visibleTemplates = templates.slice(0, effectiveVisibleCount);
  const overflowTemplates = templates.slice(effectiveVisibleCount);
  const hasOverflow = overflowTemplates.length > 0;

  const activeInOverflow = overflowTemplates.some((t) => t.id === activeTemplateId);
  const hasNewItemsInOverflow = overflowTemplates.some((t) => (newItemsCounts[t.id] || 0) > 0);
  const overflowNewItemsCount = overflowTemplates.reduce((sum, t) => sum + (newItemsCounts[t.id] || 0), 0);
  // Check if all overflow templates with new items are muted
  const allOverflowNewItemsMuted = overflowTemplates
    .filter((t) => (newItemsCounts[t.id] || 0) > 0)
    .every((t) => t.notification_sound_enabled === false);

  const renderTemplateContent = (template: SearchTemplate) => {
    const { from, to, fromCountry, toCountry } = formatLocation(template);
    const filterCount = template.filter_count || 0;
    const hasLocations = from || to;
    const isMuted = template.notification_sound_enabled === false;

    if (!hasLocations) {
      return (
        <div className={styles.tabContent}>
          <div className={styles.routeInfo}>
            <span>{template.template_title}</span>
            {isMuted && (
              <img
                src={new URL('../../../img/muted.svg', import.meta.url).href}
                alt="muted"
                className={styles.mutedIcon}
              />
            )}
          </div>
        </div>
      );
    }

    return (
      <div className={styles.tabContent}>
        <div className={styles.routeInfo}>
          {fromCountry && <span className={styles.country}>{fromCountry}, </span>}
          <span>{from}</span>
          {isMuted && (
            <img
              src={new URL('../../../img/muted.svg', import.meta.url).href}
              alt="muted"
              className={styles.mutedIcon}
            />
          )}
        </div>
        <div className={styles.routeInfo}>
          {toCountry && <span className={styles.country}>{toCountry}, </span>}
          <span>{to}</span>
          {filterCount > 0 && <span className={styles.extraFiltersCount}> (+{filterCount})</span>}
        </div>
      </div>
    );
  };

  const prefix = config.translationPrefix;

  return (
    <div className={styles.searchTabs} ref={containerRef}>
      {/* Hidden container for measuring */}
      <div className={styles.measureContainer} ref={measureRef} aria-hidden="true">
        <div className={styles.tab} data-measure="all-routes">
          <button className={`${styles.tabContent} ${styles.allRoutesBtn}`} type="button">
            {t(`${prefix}.allRoutes`)}
          </button>
        </div>
        {templates.map((template) => (
          <div key={template.id} className={`${styles.tab} ${styles.templateTab}`} data-measure="template">
            {renderTemplateContent(template)}
            <div className={styles.tabActions}>
              <button type="button" className={styles.menuBtn}>
                <ChevronDownIcon />
              </button>
              <button type="button" className={styles.mapBtn}>
                <MapIcon size={14} />
              </button>
            </div>
          </div>
        ))}
        <div className={`${styles.tab} ${styles.templateTab} ${styles.newTemplate}`} data-measure="new-template">
          <div className={styles.tabContent}>
            <div className={styles.routeInfo}>
              <span className={styles.newTemplateLabel}>{t(`${prefix}.newRoute`)}</span>
            </div>
          </div>
          <div className={styles.tabActions}>
            <button type="button" className={styles.removeBtn}>
              <CloseTabIcon />
            </button>
          </div>
        </div>
        <button type="button" className={styles.addBtn} ref={addBtnMeasureRef}>
          <PlusIcon />
        </button>
        <button type="button" className={`${styles.tab} ${styles.moreBtn}`} ref={dropdownBtnMeasureRef}>
          <ChevronDownIcon />
        </button>
      </div>

      <div className={styles.tabsWrapper} ref={tabsWrapperRef}>
        <div className={`${styles.tab} ${isAllRoutesActive ? styles.active : ''}`}>
          <button
            className={`${styles.tabContent} ${styles.allRoutesBtn} ${isAllRoutesActive ? styles.active : ''}`}
            type="button"
            onClick={() => { onSelectTemplate(null); setOpenMenus({}); setMenuReady({}); }}
          >
            {t(`${prefix}.allRoutes`)}
          </button>
        </div>

        {visibleTemplates.map((template) => {
          const newItemsCount = newItemsCounts[template.id] || 0;
          const hasNewItems = newItemsCount > 0;
          const isActive = activeTemplateId === template.id && !isCreatingNew;
          const isMuted = template.notification_sound_enabled === false;

          return (
            <div
              key={template.id}
              className={`${styles.tab} ${styles.templateTab} ${isActive ? styles.active : ''} ${
                hasNewItems ? styles.hasNewItems : ''
              } ${hasNewItems && isMuted ? styles.muted : ''}`}
              onClick={() => { onSelectTemplate(template.id); setOpenMenus({}); setMenuReady({}); }}
            >
              {renderTemplateContent(template)}
              <div className={styles.tabActions}>
                <button
                  type="button"
                  className={styles.menuBtn}
                  ref={(el) => { menuBtnRefs.current[template.id] = el; }}
                  onClick={(e) => {
                    e.stopPropagation();
                    const isOpen = openMenus[template.id];
                    if (!isOpen) {
                      setMenuReady(prev => ({ ...prev, [template.id]: false }));
                    }
                    setOpenMenus(prev => ({ ...prev, [template.id]: !isOpen }));
                  }}
                >
                  <ChevronDownIcon className={`${styles.chevron} ${openMenus[template.id] ? styles.open : ''}`} />
                </button>
                <button
                  type="button"
                  className={styles.mapBtn}
                  onClick={(e) => handleOpenMap(e, template.id)}
                  title={t(`${prefix}.showOnMap`)}
                >
                  <MapIcon size={14} />
                </button>
              </div>
              {hasNewItems && <span className={`${styles.newItemsBadge} ${isMuted ? styles.muted : ''}`}>{newItemsCount}</span>}
            </div>
          );
        })}

        {/* New template tab - before dropdown */}
        {isCreatingNew && (
          <div
            className={`${styles.tab} ${styles.templateTab} ${styles.newTemplate} ${
              isNewTemplateActive ? styles.active : ''
            }`}
          >
            <div className={styles.tabContent}>
              <div className={styles.routeInfo}>
                <span className={styles.newTemplateLabel}>{t(`${prefix}.newRoute`)}</span>
              </div>
            </div>
            <div className={styles.tabActions}>
              <button type="button" className={styles.removeBtn} onClick={handleCancelNewTemplate}>
                <CloseTabIcon />
              </button>
            </div>
          </div>
        )}

        {hasOverflow && (
          <div className={styles.dropdownContainer}>
            <button
              type="button"
              ref={dropdownBtnRef}
              className={`${styles.tab} ${styles.moreBtn} ${activeInOverflow ? styles.active : ''} ${
                hasNewItemsInOverflow ? styles.hasNewItems : ''
              } ${hasNewItemsInOverflow && allOverflowNewItemsMuted ? styles.muted : ''}`}
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            >
              <span>+{overflowTemplates.length}</span>
              {overflowNewItemsCount > 0 && <span className={`${styles.newItemsBadge} ${allOverflowNewItemsMuted ? styles.muted : ''}`}>{overflowNewItemsCount}</span>}
              <ChevronDownIcon className={`${styles.chevron} ${isDropdownOpen ? styles.open : ''}`} />
            </button>
            {isDropdownOpen &&
              ReactDOM.createPortal(
                <div
                  ref={dropdownRef}
                  className={styles.dropdown}
                  style={{
                    position: 'fixed',
                    top: dropdownPosition.top,
                    left: dropdownPosition.left,
                  }}
                >
                  {overflowTemplates.map((template) => {
                    const newItemsCount = newItemsCounts[template.id] || 0;
                    const hasNewItems = newItemsCount > 0;
                    const isActive = activeTemplateId === template.id && !isCreatingNew;
                    const isMuted = template.notification_sound_enabled === false;

                    return (
                      <div
                        key={template.id}
                        className={`${styles.dropdownItem} ${isActive ? styles.active : ''} ${
                          hasNewItems ? styles.hasNewItems : ''
                        } ${hasNewItems && isMuted ? styles.muted : ''}`}
                        onClick={() => { handleSelectFromDropdown(template.id); setDropdownOpenMenus({}); }}
                      >
                        {renderTemplateContent(template)}
                        <div className={styles.countAndActions}>
                          {hasNewItems && <span className={`${styles.dropdownBadge} ${isMuted ? styles.muted : ''}`}>{newItemsCount}</span>}
                          <div className={styles.dropdownActions}>
                            <button
                              type="button"
                              className={styles.dropdownMenuBtn}
                              ref={(el) => { dropdownMenuBtnRefs.current[template.id] = el; }}
                              onClick={(e) => {
                                e.stopPropagation();
                                const isOpen = dropdownOpenMenus[template.id];
                                if (!isOpen) {
                                  setDropdownMenuReady(prev => ({ ...prev, [template.id]: false }));
                                }
                                setDropdownOpenMenus(prev => ({ ...prev, [template.id]: !isOpen }));
                              }}
                            >
                              <ChevronDownIcon className={`${styles.chevron} ${dropdownOpenMenus[template.id] ? styles.open : ''}`} />
                            </button>
                            <button
                              type="button"
                              className={styles.dropdownMapBtn}
                              onClick={(e) => handleOpenMap(e, template.id)}
                              title={t(`${prefix}.showOnMap`)}
                            >
                              <MapIcon size={14} />
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>,
                document.body,
              )}
          </div>
        )}

        {/* Add button - right after templates */}
        {authenticated && !isCreatingNew && (
          <button type="button" className={styles.addBtn} onClick={onAddTemplate}>
            <PlusIcon />
          </button>
        )}
      </div>

      {/* Template menus */}
      {Object.entries(openMenus).map(([templateId, isOpen]) => {
        const template = templates.find(t => t.id === templateId);
        const isMuted = template?.notification_sound_enabled === false;
        const isReady = menuReady[templateId];

        return isOpen && (
          ReactDOM.createPortal(
            <div
              ref={(el) => { menuRefs.current[templateId] = el; }}
              className={styles.templateMenu}
              style={{
                position: 'fixed',
                top: menuPositions[templateId]?.top || 0,
                left: menuPositions[templateId]?.left || 0,
                visibility: isReady ? 'visible' : 'hidden',
              }}
            >
              <div className={styles.menuItem}>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggleNotifications(e, templateId);
                    setOpenMenus(prev => ({ ...prev, [templateId]: false }));
                  }}
                >
                  <span>{t(`${prefix}.${isMuted ? 'enableNotifications' : 'disableNotifications'}`)}</span>
                </button>
              </div>
              <div className={styles.menuItem}>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveTemplate(e, templateId);
                    setOpenMenus(prev => ({ ...prev, [templateId]: false }));
                  }}
                >
                  <span className={styles.deleteText}>{t(`${prefix}.deleteTab`)}</span>
                </button>
              </div>
            </div>,
            document.body
          )
        );
      })}

      {/* Dropdown template menus */}
      {Object.entries(dropdownOpenMenus).map(([templateId, isOpen]) => {
        const template = templates.find(t => t.id === templateId);
        const isMuted = template?.notification_sound_enabled === false;
        const isReady = dropdownMenuReady[templateId];

        return isOpen && (
          ReactDOM.createPortal(
            <div
              ref={(el) => { dropdownMenuRefs.current[templateId] = el; }}
              className={styles.templateMenu}
              style={{
                position: 'fixed',
                top: dropdownMenuPositions[templateId]?.top || 0,
                left: dropdownMenuPositions[templateId]?.left || 0,
                visibility: isReady ? 'visible' : 'hidden',
              }}
            >
              <div className={styles.menuItem}>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggleNotifications(e, templateId);
                    setDropdownOpenMenus(prev => ({ ...prev, [templateId]: false }));
                  }}
                >
                  <span>{t(`${prefix}.${isMuted ? 'enableNotifications' : 'disableNotifications'}`)}</span>
                </button>
              </div>
              <div className={styles.menuItem}>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveTemplate(e, templateId);
                    setDropdownOpenMenus(prev => ({ ...prev, [templateId]: false }));
                  }}
                >
                  <span className={styles.deleteText}>{t(`${prefix}.deleteTab`)}</span>
                </button>
              </div>
            </div>,
            document.body
          )
        );
      })}

      {/* Status filter icons - right aligned */}
      {authenticated && (
        <div className={styles.statusFilters} ref={statusFiltersRef}>
          <div className={styles.statusBtnWrapper}>
            <button
              type="button"
              className={`${styles.statusBtn} ${activeStatusFilter === 'watching' ? styles.active : ''}`}
              onClick={() => onStatusFilterChange?.('watching')}
            >
              <ClockOutlineIcon />
            </button>
            <span className={styles.statusTooltip}>{t(`${prefix}.statusWatching`)}</span>
          </div>
          <div className={styles.statusBtnWrapper}>
            <button
              type="button"
              className={`${styles.statusBtn} ${activeStatusFilter === 'accepted' ? styles.active : ''}`}
              onClick={() => onStatusFilterChange?.('accepted')}
            >
              <CheckmarkIcon />
            </button>
            <span className={styles.statusTooltip}>{t(`${prefix}.statusAccepted`)}</span>
          </div>
          <div className={styles.statusBtnWrapper}>
            <button
              type="button"
              className={`${styles.statusBtn} ${activeStatusFilter === 'archive' ? styles.active : ''}`}
              onClick={() => onStatusFilterChange?.('archive')}
            >
              <ArchiveIcon />
            </button>
            <span className={styles.statusTooltip}>{t(`${prefix}.statusArchive`)}</span>
          </div>
        </div>
      )}

    </div>
  );
};

export default SearchTabs;
